import { MAX_GALLERY_ITEMS, type MediaRef } from "@velizhanin-dev/bot-scenario"
import { ArrowLeft, ArrowRight, Upload, X } from "lucide-react"
import { useRef } from "react"
import type { MediaSource } from "@/entities/bot-builder"
import { Button } from "@/shared/ui/button"
import { Progress } from "@/shared/ui/progress"
import { useMediaSource, useUpload } from "./lib/use-media-upload"
import { Thumbnail } from "./media-thumbnail"

/** Альбом: 2–10 фото и видео одним сообщением, подпись, у первого. */
export function GalleryField({
	scenarioId,
	source: sourceProp,
	gallery,
	onChange,
}: {
	scenarioId?: string
	/** Откуда брать файлы, если не из сценария: например, из папки рассылок. */
	source?: MediaSource
	gallery: MediaRef[]
	onChange: (gallery: MediaRef[]) => void
}) {
	const inputRef = useRef<HTMLInputElement>(null)
	const source = useMediaSource(scenarioId, sourceProp)
	const { progress, upload } = useUpload(source)
	const move = (index: number, delta: number) => {
		const target = index + delta
		if (target < 0 || target >= gallery.length) return
		const next = [...gallery]
		;[next[index], next[target]] = [next[target], next[index]]
		onChange(next)
	}

	return (
		<div className="space-y-2">
			<div className="grid grid-cols-3 gap-2">
				{gallery.map((media, index) => (
					<div
						key={media.s3Key ?? media.telegram?.fileId ?? index}
						className="space-y-1 rounded-lg border border-border/60 p-1"
					>
						<Thumbnail source={source} media={media} size="h-16 w-full" />
						<div className="flex items-center justify-between">
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="h-6 w-6"
								aria-label="Раньше"
								disabled={index === 0}
								onClick={() => move(index, -1)}
							>
								<ArrowLeft />
							</Button>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="h-6 w-6"
								aria-label={`Убрать ${media.fileName}`}
								onClick={() => onChange(gallery.filter((_, i) => i !== index))}
							>
								<X />
							</Button>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="h-6 w-6"
								aria-label="Позже"
								disabled={index === gallery.length - 1}
								onClick={() => move(index, 1)}
							>
								<ArrowRight />
							</Button>
						</div>
					</div>
				))}
			</div>
			{progress !== null ? <Progress value={progress} className="h-1.5" /> : null}
			<input
				ref={inputRef}
				type="file"
				multiple
				accept="image/jpeg,image/png,image/webp,video/mp4"
				className="hidden"
				onChange={async (event) => {
					const files = [...(event.target.files ?? [])].slice(0, MAX_GALLERY_ITEMS - gallery.length)
					if (inputRef.current) inputRef.current.value = ""
					let next = gallery
					for (const file of files) {
						const uploaded = await upload(file, file.type === "video/mp4" ? "video" : "photo")
						if (uploaded) {
							next = [...next, uploaded]
							onChange(next)
						}
					}
				}}
			/>
			<Button
				type="button"
				variant="outline"
				size="sm"
				disabled={progress !== null || gallery.length >= MAX_GALLERY_ITEMS}
				onClick={() => inputRef.current?.click()}
			>
				<Upload />
				Добавить фото или видео · {gallery.length} из {MAX_GALLERY_ITEMS}
			</Button>
		</div>
	)
}
